import type { Component, Ref } from 'vue'
import type { ComponentProps, ComponentSlots } from 'vue-component-type-helpers'

import { computed, markRaw, ref } from 'vue'

type ModalHandlers = {
  onCancel?: () => void
  onClose?: () => void
  onConfirm?: () => void
}

export type ModalConfig<C extends Component = Component> = ModalHandlers & {
  component: C
  id?: string
  persistent?: boolean
  priority?: number
  props?: ComponentProps<C>
  routePath?: string
  slots?: Partial<ComponentSlots<C>>
}

export type ModalEntry = ModalConfig & { id: string }

export type ModalRegistration<C extends Component = Component> = ModalHandlers & {
  component: C
  id?: string
  priority?: number
  props?: () => ComponentProps<C>
  slots?: Partial<ComponentSlots<C>>
  when?: () => boolean
}

export type StoredRegistration = ModalRegistration & { id: string }

export type ResolvedModal = {
  component: Component
  id: string
  props?: Record<string, unknown>
  slots?: Record<string, unknown>
}

type ModalCallback = keyof ModalHandlers

export const useModalIsolated = (
  activeModal: Ref<ModalEntry | undefined>,
  registrations: Ref<StoredRegistration[]> = ref([]),
) => {
  const openModal = <C extends Component>(config: ModalConfig<C>) => {
    const id = config.id ?? uniqueId('modalisolated-')
    if (activeModal.value?.id === id) return id

    activeModal.value = {
      ...config,
      component: markRaw(config.component as object) as Component,
      id,
    }

    return id
  }

  const resolveModal = (id: string, callback: ModalCallback) => {
    if (activeModal.value?.id === id) {
      const handler = activeModal.value[callback]
      activeModal.value = undefined
      handler?.()
      return
    }

    const registration = registrations.value.find((entry) => entry.id === id)
    registration?.[callback]?.()
  }

  const cancelModal = (id: string) => resolveModal(id, 'onCancel')

  const closeModal = (id: string) => resolveModal(id, 'onClose')

  const confirmModal = (id: string) => resolveModal(id, 'onConfirm')

  const registerModal = <C extends Component>(registration: ModalRegistration<C>) => {
    const id = registration.id ?? uniqueId('modal-')

    const stored: StoredRegistration = {
      ...(registration as ModalRegistration),
      component: markRaw(registration.component as object) as Component,
      id,
    }

    registrations.value = [...registrations.value.filter((entry) => entry.id !== id), stored]

    return {
      id,
      unregister: () => {
        registrations.value = registrations.value.filter((entry) => entry.id !== id)
      },
    }
  }

  const resolvedModal = computed<ResolvedModal | undefined>(() => {
    const candidates: { declarative: boolean; modal: ResolvedModal; priority: number }[] = []

    if (activeModal.value) {
      candidates.push({
        declarative: false,
        modal: {
          component: activeModal.value.component,
          id: activeModal.value.id,
          props: activeModal.value.props as Record<string, unknown> | undefined,
          slots: activeModal.value.slots as Record<string, unknown> | undefined,
        },
        priority: activeModal.value.priority ?? 0,
      })
    }

    for (const registration of registrations.value) {
      if (registration.when && !registration.when()) continue

      candidates.push({
        declarative: true,
        modal: {
          component: registration.component,
          id: registration.id,
          props: registration.props?.(),
          slots: registration.slots as Record<string, unknown> | undefined,
        },
        priority: registration.priority ?? 0,
      })
    }

    if (candidates.length === 0) return undefined

    candidates.sort((a, b) => {
      if (b.priority !== a.priority) return b.priority - a.priority
      return Number(b.declarative) - Number(a.declarative)
    })

    return candidates[0].modal
  })

  return {
    activeModal,
    cancelModal,
    closeModal,
    confirmModal,
    openModal,
    registerModal,
    registrations,
    resolvedModal,
  }
}
